import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { SharedApprovalService } from '@shared/services/approval.service';
import * as ApprovalActions from './approval.actions';
import * as ApprovalSelectors from './approval.selectors';
import { ApprovalService } from '../services/approval.service';
import type { ApprovalOrderBy } from '../models/approval.model';

/**
 * Approval Effects
 * Handles side effects for approval-related actions
 */
@Injectable()
export class ApprovalEffects {
  private readonly translate = inject(TranslateService);
  private readonly sharedApprovalService = inject(SharedApprovalService);
  /**
   * Load approval requests effect
   * Triggers API call based on active mode and current filters
   */
  loadApprovalRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.loadApprovalRequests),
      withLatestFrom(
        this.store.select(ApprovalSelectors.selectActiveMode),
        this.store.select(ApprovalSelectors.selectPageIndex),
        this.store.select(ApprovalSelectors.selectPageSize),
        this.store.select(ApprovalSelectors.selectCurrentFilters),
        this.store.select(ApprovalSelectors.selectSort)
      ),
      switchMap(([action, activeMode, pageIndex, pageSize, filters, sort]) => {
        const mode = action.mode ?? activeMode;
        const page = pageIndex + 1; // API uses 1-based pagination
        const orderBy = (sort.active || 'createdAt') as ApprovalOrderBy;
        const order = sort.direction || 'DESC';

        // Dispatch to the right endpoint based on the active audience.
        // `getMyRequests` is the `'myRequests'` (requester) audience;
        // the other two are the approver-side endpoints.
        const request$ =
          mode === 'all'
            ? this.approvalService.getApprovalRequests(
                page,
                pageSize,
                filters,
                orderBy,
                order
              )
            : mode === 'myRequests'
              ? this.approvalService.getMyRequests(
                  page,
                  pageSize,
                  filters,
                  orderBy,
                  order
                )
              : this.approvalService.getMyApprovalRequests(
                  page,
                  pageSize,
                  filters,
                  orderBy,
                  order
                );

        return request$.pipe(
          map(response =>
            ApprovalActions.loadApprovalRequestsSuccess({
              data: response.data,
              pagination: {
                page: response.pagination.page,
                limit: response.pagination.limit,
                total: response.pagination.total,
              },
            })
          ),
          catchError(error =>
            of(
              ApprovalActions.loadApprovalRequestsFailure({
                error:
                  error.error?.message ||
                  error.message ||
                  this.translate.instant('approvals.errors.loadList'),
              })
            )
          )
        );
      })
    )
  );

  /**
   * Reload on tab change
   */
  reloadOnTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.setActiveTab),
      map(({ mode }) => ApprovalActions.loadApprovalRequests({ mode }))
    )
  );

  /**
   * Reload on pagination change
   */
  reloadOnPaginationChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.setPage, ApprovalActions.setPageSize),
      map(() => ApprovalActions.loadApprovalRequests({}))
    )
  );

  /**
   * Reload on filter change
   */
  reloadOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ApprovalActions.setFilters,
        ApprovalActions.clearFilters,
        ApprovalActions.removeFilterChip
      ),
      map(() => ApprovalActions.loadApprovalRequests({}))
    )
  );

  /**
   * Reload on search change
   */
  reloadOnSearchChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.setSearch),
      map(() => ApprovalActions.loadApprovalRequests({}))
    )
  );

  /**
   * Reload on sort change
   */
  reloadOnSortChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.setSort),
      map(() => ApprovalActions.loadApprovalRequests({}))
    )
  );

  /**
   * Load requesters for filter dropdown
   */
  loadRequesters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.loadRequesters),
      switchMap(() =>
        this.approvalService.getRequesters().pipe(
          map(response =>
            ApprovalActions.loadRequestersSuccess({
              requesters: response.data,
            })
          ),
          catchError(error =>
            of(
              ApprovalActions.loadRequestersFailure({
                error:
                  error.error?.message ||
                  error.message ||
                  this.translate.instant('approvals.errors.loadRequesters'),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Close filter modal on apply/clear
   */
  closeModalOnFilterAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.setFilters, ApprovalActions.clearFilters),
      map(() => ApprovalActions.closeFilterModal())
    )
  );

  // ---------------------------------------------------------------------------
  // Detail (used by the my-requests detail page).
  //
  // We hit the shared approval endpoint so the wire shape matches what
  // `all-approvals` / `my-approvals` render. Container components
  // dispatch `loadApprovalRequestDetail` on init and subscribe to the
  // detail selectors below.
  // ---------------------------------------------------------------------------
  loadApprovalRequestDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.loadApprovalRequestDetail),
      switchMap(({ id }) =>
        this.sharedApprovalService.getApprovalRequestById(id).pipe(
          map(response => {
            if (!response?.data) {
              return ApprovalActions.loadApprovalRequestDetailFailure({
                error: this.translate.instant('approvals.errors.dataNotFound'),
              });
            }
            return ApprovalActions.loadApprovalRequestDetailSuccess({
              detail: response.data,
            });
          }),
          catchError(error =>
            of(
              ApprovalActions.loadApprovalRequestDetailFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant('approvals.errors.loadDetail'),
              })
            )
          )
        )
      )
    )
  );

  // ---------------------------------------------------------------------------
  // Cancel (my-requests audience).
  //
  // Navigation / toasts intentionally live in the calling component so
  // we keep this effect focused on the network call (see CLAUDE.md —
  // side-effecty UI work belongs in dedicated `tap` effects or in
  // components, not buried in data effects).
  // ---------------------------------------------------------------------------
  cancelApprovalRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApprovalActions.cancelApprovalRequest),
      switchMap(({ id, reason }) =>
        this.approvalService.cancelApprovalRequest(id, reason).pipe(
          map(() => ApprovalActions.cancelApprovalRequestSuccess({ id })),
          catchError(error =>
            of(
              ApprovalActions.cancelApprovalRequestFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  this.translate.instant('myRequests.errors.cancelFailed'),
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private approvalService: ApprovalService
  ) {}
}
